import { IMenu, IMenuService, MenuId, MenuItemAction, MenuRegistry, SubmenuItemAction } from "../../../platform/actions/common/actions";
import { localize } from "../../../base/common/nls";
import { IContextKeyService } from "../../../platform/contextkey/common/contextkey";
import { Disposable, DisposableStore } from "../../../base/common/lifecycle";
import { IsMacNativeContext } from "../../../platform/contextkey/common/contextkeys";
import { IAction, Separator } from "../../../base/common/actions";
import { URI } from "../../../base/common/uri";
import { mnemonicMenuLabel } from "../../../base/common/labels";
import { IProjectsService, IRecentlyOpened } from "../../services/projects/common/projects";
import { RunOnceScheduler } from "../../../base/common/async";
import { ILogService } from "../../../platform/log/common/log";
import { windowOpenRecentId as OpenRecentActionID } from "../../browser/actionIds";
import { getFlatContextMenuActions } from "./menuEntryActionViewItem";
import { IMenubarKeybinding, IMenubarMenu, IMenubarMenuItemAction, IMenubarMenuItemSubmenu, IMenubarMenuRecentItemAction } from "../../../platform/menubar/common/menubar";
import { getIconId, isICommandActionToggleInfo } from "../../../platform/action/common/action";
import { CommandService } from "../../../platform/commands/common/commandService";
import { MenuStyleConfiguration } from "../../../platform/window/common/window";
import { Emitter } from "../../../base/common/events";
import { IKeybindingService } from "../../../platform/keybinding/common/keybindingService";

export type IOpenRecentAction = IAction & { uri: URI; remoteAuthority?: string };

MenuRegistry.appendMenuItem(MenuId.MenubarMainMenu, {
	submenu: MenuId.MenubarFileMenu,
	title: {
		value: 'File',
		original: 'File',
		mnemonicTitle: localize({ key: 'mFile', comment: ['&& denotes a mnemonic'] }, "&&File"),
	},
	order: 1
});
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
	submenu: MenuId.MenubarFileExportMenu,
	title: {
		value: 'Export',
		original: 'Export',
		mnemonicTitle: localize({ key: 'mExport', comment: ['&& denotes a mnemonic'] }, "&&Export"),
	},
	icon: { id: "export" },
	order: 10
});


MenuRegistry.appendMenuItem(MenuId.MenubarMainMenu, {
	submenu: MenuId.MenubarEditMenu,
	title: {
		value: 'Edit',
		original: 'Edit',
		mnemonicTitle: localize({ key: 'mEdit', comment: ['&& denotes a mnemonic'] }, "&&Edit")
	},
	order: 2
});

MenuRegistry.appendMenuItem(MenuId.MenubarMainMenu, {
	submenu: MenuId.MenubarSelectionMenu,
	title: {
		value: 'Selection',
		original: 'Selection',
		mnemonicTitle: localize({ key: 'mSelection', comment: ['&& denotes a mnemonic'] }, "&&Selection")
	},
	order: 3
});

MenuRegistry.appendMenuItem(MenuId.MenubarMainMenu, {
	submenu: MenuId.MenubarViewMenu,
	title: {
		value: 'View',
		original: 'View',
		mnemonicTitle: localize({ key: 'mView', comment: ['&& denotes a mnemonic'] }, "&&View")
	},
	order: 4
});

MenuRegistry.appendMenuItem(MenuId.MenubarMainMenu, {
	submenu: MenuId.MenubarLayoutMenu,
	title: {
		value: 'Layout',
		original: 'Layout',
		mnemonicTitle: localize({ key: 'mLayout', comment: ['&& denotes a mnemonic'] }, "&&Layout")
	},
	order: 4
});

MenuRegistry.appendMenuItem(MenuId.MenubarMainMenu, {
	submenu: MenuId.MenubarGoMenu,
	title: {
		value: 'Go',
		original: 'Go',
		mnemonicTitle: localize({ key: 'mGoto', comment: ['&& denotes a mnemonic'] }, "&&Go")
	},
	order: 5
});

MenuRegistry.appendMenuItem(MenuId.MenubarMainMenu, {
	submenu: MenuId.MenubarTerminalMenu,
	title: {
		value: 'Terminal',
		original: 'Terminal',
		mnemonicTitle: localize({ key: 'mTerminal', comment: ['&& denotes a mnemonic'] }, "&&Terminal")
	},
	order: 7
});

MenuRegistry.appendMenuItem(MenuId.MenubarMainMenu, {
	submenu: MenuId.MenubarHelpMenu,
	title: {
		value: 'Help',
		original: 'Help',
		mnemonicTitle: localize({ key: 'mHelp', comment: ['&& denotes a mnemonic'] }, "&&Help")
	},
	order: 8
});

MenuRegistry.appendMenuItem(MenuId.MenubarMainMenu, {
	submenu: MenuId.MenubarPreferencesMenu,
	title: {
		value: 'Preferences',
		original: 'Preferences',
		mnemonicTitle: localize({ key: 'mPreferences', comment: ['&& denotes a mnemonic'] }, "Preferences")
	},
	when: IsMacNativeContext,
	order: 9
});








// MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
// 	command: {
// 		id: 'test.command',
// 		title: 'Test Command',
// 	},
// 	title: {
// 		value: 'Test Command',
// 		original: 'Test Command',
// 		mnemonicTitle: localize({ key: 'mTestCommand', comment: ['&& denotes a mnemonic'] }, "&&Test Command"),
// 	},
// 	order: 99
// })

// MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
// 	command: {
// 		id: 'test.command',
// 		title: 'Test Command',
// 	},
// 	title: {
// 		value: 'Test Command',
// 		original: 'Test Command',
// 		mnemonicTitle: localize({ key: 'mTestCommand', comment: ['&& denotes a mnemonic'] }, "&&Test Command"),
// 	},
// 	order: 2

// })



export class MenubarControl extends Disposable {

	private readonly _onDidChange = this._register(new Emitter<void>());
	public readonly onDidChange = this._onDidChange.event;

	public get mainMenu(): IMenu { return this._mainMenu; }
	
	private keybindings: { [id: string]: IMenubarKeybinding | undefined } = {};

	getTopLevelItems(): string[] {
		return Object.keys(this.topLevelTitles);
	}

	getMenuItemActions(item: string): IMenubarMenu {
		const items = this.menus[item];
		if (items) {
			const groups = items.getActions({ shouldForwardArgs: true });
			const menubarMenu: IMenubarMenu = { items: [] };
			const menuActions = getFlatContextMenuActions(groups);
			this.populateMenuItems(menuActions, menubarMenu, this.keybindings);
			return menubarMenu;
		}
		return { items: [] };
	}

	private populateMenuItems(menuActions: readonly IAction[], menuToPopulate: IMenubarMenu, keybindings: { [id: string]: IMenubarKeybinding | undefined }) {
		for (const menuItem of menuActions) {
			if (menuItem instanceof Separator) {
				menuToPopulate.items.push({ id: 'needle.menubar.separator' });
			} else if (menuItem instanceof MenuItemAction || menuItem instanceof SubmenuItemAction) {

				// use mnemonicTitle whenever possible
				const title = typeof menuItem.item.title === 'string'
					? menuItem.item.title
					: menuItem.item.title.mnemonicTitle ?? menuItem.item.title.value;

				if (menuItem instanceof SubmenuItemAction) {
					const submenu = { items: [] };

					this.populateMenuItems(menuItem.actions, submenu, keybindings);

					if (submenu.items.length > 0) {
						const menubarSubmenuItem: IMenubarMenuItemSubmenu = {
							id: menuItem.id,
							label: title,
							submenu
						};

						menuToPopulate.items.push(menubarSubmenuItem);
					}
				} else {
					if (menuItem.id === OpenRecentActionID) {
						const recent = this.recentlyOpened;
						for (let i = 0; i < recent.files.length && i < 10; i++) {
							const file = recent.files[i];
							// this.logService.debug('MenubarControl: Adding recent file to menubar', file);
							const uri = file.fileUri;
							const menubarMenuItem: IMenubarMenuRecentItemAction = {
								id: OpenRecentActionID,
								label: mnemonicMenuLabel(file.label ?? file.fileUri.fsPath),
								enabled: true,
								uri: file.fileUri,
								click: () => {
									this.logService.debug("OPEN RECENT", uri.toString());
									CommandService.get().executeCommand(OpenRecentActionID, uri);
								}
							};
							menuToPopulate.items.push(menubarMenuItem);
						}
					}


					const menubarMenuItem: IMenubarMenuItemAction = {
						id: menuItem.id,
						label: title,
						tooltip: typeof menuItem.item.tooltip === "string" ? menuItem.item.tooltip : undefined,
						icon: menuItem.item.icon ? getIconId(menuItem.item.icon) : undefined,
						click: () => {
							CommandService.get().executeCommand(menuItem.id, undefined);
						}
					};

					if (menuItem.checked === true && isICommandActionToggleInfo(menuItem.item.toggled)) {
						menubarMenuItem.label = menuItem.item.toggled.mnemonicTitle ?? menuItem.item.toggled.title ?? title;
					}

					if (menuItem.checked) {
						menubarMenuItem.checked = true;
					}

					if (!menuItem.enabled) {
						menubarMenuItem.enabled = false;
					}

					const keybinding = keybindings[menuItem.id] = this.getMenubarKeybinding(menuItem.id);
					if (keybinding) {
						menubarMenuItem.keybinding = keybinding;
					}
					
					menuToPopulate.items.push(menubarMenuItem);
				}
			}
		}
	}

	private getMenubarKeybinding(id: string): IMenubarKeybinding | undefined {
		const binding = this.keybindingService.lookupKeybinding(id);
		if (!binding) {
			return undefined;
		}

		// first try to resolve a native accelerator
		// const electronAccelerator = binding.getElectronAccelerator();
		// if (electronAccelerator) {
		// 	return { label: electronAccelerator, userSettingsLabel: binding.getUserSettingsLabel() ?? undefined };
		// }

		// we need this fallback to support keybindings that cannot show in electron menus (e.g. chords)
		const acceleratorLabel = binding.getLabel();
		if (acceleratorLabel) {
			return { label: acceleratorLabel, isNative: false, }// userSettingsLabel: binding.getUserSettingsLabel() ?? undefined };
		}

		

		return undefined;
	}


	protected _mainMenu: IMenu;
	protected menus: {
		[index: string]: IMenu | undefined;
	} = {};

	protected topLevelTitles: { [menu: string]: string } = {};

	protected readonly mainMenuDisposables: DisposableStore;

	protected recentlyOpened: IRecentlyOpened = { files: [], projects: [] };

	protected menuUpdater: RunOnceScheduler;

	constructor(
		@IMenuService protected readonly menuService: IMenuService,
		@IContextKeyService protected readonly contextKeyService: IContextKeyService,
		@IKeybindingService	 protected readonly keybindingService: IKeybindingService,
		@ILogService protected readonly logService: ILogService,
		@IProjectsService protected readonly projectsService: IProjectsService | null,
	) {
		super();

		this._mainMenu = this._register(this.menuService.createMenu(MenuId.MenubarMainMenu, this.contextKeyService, { emitEventsForSubmenuChanges: true }));
		this.mainMenuDisposables = this._register(new DisposableStore());

		this.registerListeners();

		this.setupMainMenu();

		this.menuUpdater = this._register(new RunOnceScheduler(() => this.doUpdateMenubar(false), 200));

		(async () => {
			if (this.projectsService) {
				this.recentlyOpened = await this.projectsService.getRecentlyOpened();
				this.logService.debug('MenubarControl: Initial recently opened', this.recentlyOpened);
				this.doUpdateMenubar();
			}
		})();
	}

	protected registerListeners(): void {

		// Listen for changes on the main menu
		this._register(this._mainMenu.onDidChange((evt) => {
			if (evt.isStructuralChange) {
				this.setupMainMenu()
			}
		}));

		// Listen for changes in recently opened menu
		if (this.projectsService) this._register(this.projectsService.onDidChangeRecentlyOpened(() => { this.onDidChangeRecentlyOpened(); }));
		// this._register(this.mainMenu.onDidChange(() => { this.setupMainMenu(); this.doUpdateMenubar(true); }));

	}

	protected updateMenubar(): void {
		this.menuUpdater.schedule();
	}

	protected setupMainMenu() {

		this.mainMenuDisposables.clear();
		this.menus = {};
		this.topLevelTitles = {};
		const [, mainMenuActions] = this.mainMenu.getActions()[0];
		for (const mainMenuAction of mainMenuActions) {
			if (mainMenuAction instanceof SubmenuItemAction && typeof mainMenuAction.item.title !== 'string') {
				const submenu = this.mainMenuDisposables.add(this.menuService.createMenu(mainMenuAction.item.submenu, this.contextKeyService, { emitEventsForSubmenuChanges: true }));

				this.menus[mainMenuAction.item.title.original] = submenu;
				this.topLevelTitles[mainMenuAction.item.title.original] = mainMenuAction.item.title.mnemonicTitle ?? mainMenuAction.item.title.value;
			}
		}


		// for (const topLevelMenuName of Object.keys(this.topLevelTitles)) {
		// 	const menu = this.menus[topLevelMenuName];
		// 	if (menu) {
		// 		this.mainMenuDisposables.add(menu.onDidChange(() => this.updateMenubar()));
		// 	}
		// }
	}

	protected doUpdateMenubar(firstTime?: boolean): void {
	}

	protected onDidChangeRecentlyOpened(): void {

		this.logService.debug('MenubarControl#onDidChangeRecentlyOpened');

		// Do not update recently opened when the menubar is hidden #108712
		// if (!this.menubarHidden) 
		{
			this.projectsService?.getRecentlyOpened().then(recentlyOpened => {
				this.recentlyOpened = recentlyOpened;
				this.updateMenubar();
			});
		}
	}

}


export type MenubarItemActionsData = {
	groupName: string;
	actions: Array<SubmenuItemAction | MenuItemAction>;
}
export function isMenubarItemActionsData(obj: any): obj is MenubarItemActionsData {
	if (typeof obj !== 'object' || obj === null) return false;
	if (!('groupName' in obj) || typeof obj.groupName !== 'string') return false;
	if (!('actions' in obj) || !Array.isArray(obj.actions)) return false;
	return true;
}